import { useState } from 'react';
import { Button } from '@/components/common';
import { NewOperationButton } from '@/features/operations';
import { useIncoming } from './useIncoming';
import { IncomingList } from './IncomingList';
import { IncomingDetailModal } from './IncomingDetailModal';
import { SimulationForm } from './SimulationForm';
import type { IncomingPaymentDto } from './types';

// ---------------------------------------------------------------------------
// IncomingPage — route: /incoming
// ---------------------------------------------------------------------------

export function IncomingPage() {
  const {
    data,
    total,
    isLoading,
    error,
    filters,
    setStatus,
    setDateRange,
    setPage,
    setLimit,
    refresh,
    approvePayment,
    rejectPayment,
    simulateIncoming,
    isApproving,
    isRejecting,
    clients,
    isLoadingClients,
    clientNameById,
  } = useIncoming();

  const [selectedPayment, setSelectedPayment] = useState<IncomingPaymentDto | null>(null);

  const receiverName = selectedPayment
    ? (selectedPayment.client_name ?? clientNameById.get(selectedPayment.client_id) ?? null)
    : null;

  return (
    <div data-testid="incoming-page">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#333]">Incoming Payments</h2>
          <p className="mt-1 text-sm text-[#999]">Incoming wire transfers and manual entries</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Manual incoming entries are booked from New Operation, which
              carries its own permission gate. Refresh is read-only. */}
          <NewOperationButton />
          <Button variant="outline" onClick={refresh} data-testid="refresh-btn">
            Refresh
          </Button>
        </div>
      </div>

      {/* Date range filters */}
      <div className="mb-4 flex items-center gap-4">
        <div className="flex items-center gap-2">
          <label htmlFor="date-from" className="text-xs font-semibold text-[#666]">
            From
          </label>
          <input
            id="date-from"
            type="date"
            value={filters.dateFrom ?? ''}
            onChange={(e) => setDateRange(e.target.value || undefined, filters.dateTo)}
            className="rounded-lg border border-[#e0e0e0] px-3 py-1.5 text-sm outline-none focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]"
            data-testid="date-from"
          />
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="date-to" className="text-xs font-semibold text-[#666]">
            To
          </label>
          <input
            id="date-to"
            type="date"
            value={filters.dateTo ?? ''}
            onChange={(e) => setDateRange(filters.dateFrom, e.target.value || undefined)}
            className="rounded-lg border border-[#e0e0e0] px-3 py-1.5 text-sm outline-none focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]"
            data-testid="date-to"
          />
        </div>
        {(filters.dateFrom || filters.dateTo) && (
          <button
            onClick={() => setDateRange(undefined, undefined)}
            className="text-xs text-[#6B0F1A] hover:underline"
            data-testid="clear-dates"
          >
            Clear dates
          </button>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div
          className="mb-4 flex items-center justify-between rounded-lg border border-[#ef9a9a] bg-[#ffebee] px-4 py-3 text-sm text-[#c62828]"
          role="alert"
          data-testid="error-banner"
        >
          <span>Failed to load incoming payments. Check connection.</span>
          <Button variant="outline" size="sm" onClick={refresh} data-testid="retry-btn">
            Retry
          </Button>
        </div>
      )}

      {/* Incoming list table */}
      <IncomingList
        data={data}
        total={total}
        isLoading={isLoading}
        page={filters.page}
        limit={filters.limit}
        statusFilter={filters.status}
        onStatusFilter={setStatus}
        onPageChange={setPage}
        onPageSizeChange={setLimit}
        clientNameById={clientNameById}
        onRowClick={setSelectedPayment}
      />

      {/* Detail modal — opens on row click; owns Approve / Reject confirmation */}
      <IncomingDetailModal
        payment={selectedPayment}
        receiverName={receiverName}
        isOpen={selectedPayment !== null}
        onClose={() => setSelectedPayment(null)}
        onApprove={approvePayment}
        onReject={rejectPayment}
        isApproving={isApproving}
        isRejecting={isRejecting}
      />

      {/* Empty state */}
      {!isLoading && !error && data.length === 0 && (
        <div className="mt-8 text-center text-[13px] text-[#999]" data-testid="empty-state">
          No incoming payments found
        </div>
      )}

      {/* Dev-only simulation tools — tree-shaken in production builds */}
      {import.meta.env.DEV && (
        <section
          className="mt-10 border-t-2 border-dashed border-[#ff9800] pt-6"
          data-testid="dev-tools-section"
        >
          <h2 className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#ff9800]">
            <span className="inline-block rounded bg-[#fff3e0] px-2 py-0.5 text-xs">DEV</span>
            Development Tools
          </h2>
          <SimulationForm
            clients={clients}
            isLoadingClients={isLoadingClients}
            onSimulate={simulateIncoming}
            onSuccess={refresh}
          />
        </section>
      )}
    </div>
  );
}
